import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { mget, mput, mpost, errMsg, fmtTs } from "@/manager/lib";
import { PageHeader, Card, Field, goldBtn } from "@/manager/ui";
import DigestCard from "@/manager/components/DigestCard";

const OFFSETS = [["guests_confirm", "Confirmar invitados"], ["staff_assign", "Asignar camareros"], ["drinks_review", "Revisar bebidas"],
  ["setup", "Preparar montaje"], ["payment_review", "Revisar cobro"], ["cleaning", "Programar limpieza"], ["consumption_log", "Registrar consumos"], ["close_finance", "Cerrar económicamente"]];

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [logs, setLogs] = useState([]);
  const [audit, setAudit] = useState([]);
  const loadSide = () => { mget("/sync/logs").then(setLogs); mget("/audit").then(setAudit); };
  useEffect(() => { mget("/settings").then(setS); loadSide(); }, []);
  if (!s) return null;
  const n = (k) => (e) => setS({ ...s, [k]: Number(e.target.value) });
  const save = async () => { try { setS(await mput("/settings", s)); toast.success("Configuración guardada"); loadSide(); } catch (e) { toast.error(errMsg(e)); } };
  const sync = async () => { const r = await mpost("/sync"); toast.success(`Sincronizado: ${r.created} nuevos, ${r.updated} actualizados, ${r.cancelled} cancelados, ${r.leads} solicitudes`); loadSide(); };
  const seed = async () => { try { const r = await mpost("/seed-demo"); toast.success(`Cargados ${r.products} productos, proveedores y personal de ejemplo`); loadSide(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="manager-settings">
      <PageHeader eyebrow="Configuración" title="Reglas y automatizaciones" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 space-y-4">
          <h3 className="font-display text-lg">Reglas operativas</h3>
          <Field label="Máximo de invitados para 1 camarero (por encima: 2)"><Input type="number" value={s.waiter_threshold} onChange={n("waiter_threshold")} data-testid="set-waiter-threshold" /></Field>
          <Field label="Días de antelación para confirmar proveedores"><Input type="number" value={s.supplier_review_days} onChange={n("supplier_review_days")} data-testid="set-supplier-days" /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch checked={s.cleaning_after_sala} onCheckedChange={(v) => setS({ ...s, cleaning_after_sala: v })} data-testid="set-cleaning" /> Crear limpieza después de cada evento en la sala</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={s.stock_alerts} onCheckedChange={(v) => setS({ ...s, stock_alerts: v })} data-testid="set-stock-alerts" /> Alertas cuando el stock llegue al mínimo</label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fee conceptualización externos (€)"><Input type="number" value={s.concept_fee} onChange={n("concept_fee")} data-testid="set-concept-fee" /></Field>
            <Field label="Fee coordinación externos (€)"><Input type="number" value={s.coordination_fee} onChange={n("coordination_fee")} data-testid="set-coordination-fee" /></Field>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-display text-lg mb-1">Plazos de tareas automáticas</h3>
          <p className="text-xs text-[#6b6a66] mb-4">Días respecto a la fecha del evento (negativo = antes, positivo = después).</p>
          <div className="grid grid-cols-2 gap-3">
            {OFFSETS.map(([k, l]) => (
              <Field key={k} label={l}><Input type="number" value={s.offsets[k]} onChange={(e) => setS({ ...s, offsets: { ...s.offsets, [k]: Number(e.target.value) } })} data-testid={`set-offset-${k}`} /></Field>
            ))}
          </div>
        </Card>
      </div>
      <Button className={`${goldBtn} mt-6`} onClick={save} data-testid="set-save">Guardar configuración</Button>

      <div className="mt-10"><DigestCard /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <Card className="p-5">
          <h3 className="font-display text-lg mb-1">Conexión con la web</h3>
          <p className="text-xs text-[#6b6a66] mb-3">Solo lectura: los eventos de la sala, los eventos externos y las solicitudes de la web se copian aquí automáticamente (cada minuto al usar el Manager). Nunca se escribe en la web.</p>
          <Button variant="outline" className="rounded-full gap-2 mb-3" onClick={sync} data-testid="set-sync"><RefreshCw size={14} /> Sincronizar ahora</Button>
          {logs.slice(0, 6).map((l) => <p key={l.id} className={`text-xs py-0.5 ${l.ok ? "text-[#6b6a66]" : "text-rose-700"}`}>{fmtTs(l.at)} · {l.ok ? `+${l.created} nuevos, ${l.updated} act., ${l.cancelled} canc., ${l.leads} solicitudes` : `Error: ${l.error}`}</p>)}
          <div className="border-t border-[#f0ece4] mt-4 pt-4">
            <p className="text-xs text-[#6b6a66] mb-2">¿Empiezas de cero? Carga un catálogo de bebidas y consumibles con mínimos, 2 proveedores y 3 personas marcados como [DEMO] para editarlos.</p>
            <Button variant="outline" className="rounded-full gap-2" onClick={seed} data-testid="set-seed"><Database size={14} /> Cargar datos de ejemplo</Button>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-display text-lg mb-3">Registro de cambios</h3>
          <div className="max-h-72 overflow-y-auto">
            {audit.map((a) => <p key={a.id} className="text-xs text-[#6b6a66] py-0.5">{fmtTs(a.at)} · {a.user} · {a.action} {a.entity} {a.detail && `· ${a.detail}`}</p>)}
          </div>
        </Card>
      </div>
    </div>
  );
}
